/**
 * Hinnan ratkaisu ja arvon laskenta (suunnitelma 5.2, 5.6).
 *
 * Rahaa ei käsitellä `number`-tyyppinä missään kerroksessa. Rajapinnoilla
 * eurot ja tuntihinnat kulkevat desimaalimerkkijonoina (`"115.00"`), ja
 * laskenta tehdään Decimalilla. Tämä tiedosto on ainoa paikka, jossa
 * kirjauksen arvo lasketaan — lomakkeen esikatselu, palvelimen tallennus,
 * raportit ja viennit käyttävät kaikki tätä.
 */
// Nimetty tuonti, ei oletustuonti: oletustuonti ratkeaa NodeNext-moodissa
// moduulin nimiavaruudeksi eikä luokaksi, jolloin Decimal.clone katoaa.
import { Decimal } from 'decimal.js';
/**
 * Oma Decimal-kloonimme: pyöristys puolikkaista ylöspäin itseisarvoltaan,
 * sama kuin PostgreSQLin `round(numeric, 2)`. Kloonaus estää sen, että joku
 * muu kirjasto muuttaisi globaalia asetusta jalkojemme alta.
 */
const D = Decimal.clone({
    precision: 34,
    rounding: Decimal.ROUND_HALF_UP,
    toExpNeg: -30,
    toExpPos: 30,
});
/**
 * Hinnan periytymisketju: projekti → tehtävälaji → asiakas → tyhjä.
 *
 * Projekti voittaa tehtävälajin tarkoituksella (suunnitelma 5.2): projektin
 * hinta on kertaluonteinen, tietoinen sopimus, tehtävälajin hinta asetetaan
 * kerran ja unohdetaan.
 *
 * Yleistä oletushintaa ei ole. Hinnaton laskutettava kirjaus tallennetaan
 * hinnattomana ja nousee esiin hinnoittelemattomien listalla — arvattu hinta
 * laskutettaisiin hiljaa väärin.
 */
export function resolveHourlyRate(input) {
    if (!input.billable) {
        return { hourlyRate: null, source: 'NONE' };
    }
    if (input.project?.hourlyRate != null) {
        return { hourlyRate: input.project.hourlyRate, source: 'PROJECT' };
    }
    if (input.taskType.hourlyRate != null) {
        return { hourlyRate: input.taskType.hourlyRate, source: 'TASK_TYPE' };
    }
    if (input.customer.defaultHourlyRate != null) {
        return { hourlyRate: input.customer.defaultHourlyRate, source: 'CUSTOMER' };
    }
    return { hourlyRate: null, source: 'NONE' };
}
/**
 * Yhden kirjauksen arvo euroina.
 *
 * Pyöristys tehdään rivikohtaisesti, ja summat lasketaan pyöristetyistä
 * riviarvoista (`sumMoney`). Toisin päin tulos eroaisi laskun riveistä
 * sentillä siellä täällä, eikä asiakkaan lasku täsmäisi raporttiin.
 */
export function entryValue(minutes, hourlyRate, billable) {
    if (!billable || hourlyRate == null)
        return '0.00';
    // minutes on aina 30:n monikerta, joten /60 on eksakti desimaalissa.
    return new D(minutes).div(60).mul(new D(hourlyRate)).toFixed(2);
}
/** Summaa valmiiksi pyöristetyt riviarvot. Ks. entryValue. */
export function sumMoney(values) {
    return values.reduce((total, value) => total.plus(new D(value)), new D(0)).toFixed(2);
}
/** Tunnit henkilötyöpäiviksi (suunnitelma 4.4). Johdettu luku, ei tallennettu. */
export function hoursToDays(hours, workdayHours) {
    const divisor = new D(workdayHours);
    if (divisor.isZero())
        return '0.00';
    return new D(hours).div(divisor).toFixed(2);
}
export function minutesToHoursDecimal(minutes) {
    return new D(minutes).div(60).toFixed(1);
}
/** Osuus kahden luvun välillä, esim. budjetin käyttöaste. `null` jos jakaja puuttuu. */
export function share(used, total) {
    if (total == null)
        return null;
    const divisor = new D(total);
    if (divisor.isZero())
        return null;
    return new D(used).div(divisor).toDecimalPlaces(4).toNumber();
}
export function isNonNegativeDecimalString(value) {
    try {
        const parsed = new D(value);
        return parsed.isFinite() && parsed.greaterThanOrEqualTo(0);
    }
    catch {
        return false;
    }
}
/** Normalisoi käyttäjän syöttämän luvun kantaan kelpaavaksi (pilkku → piste). */
export function normalizeDecimalInput(value, decimals = 2) {
    const cleaned = value.replace(/\s/g, '').replace(',', '.');
    if (cleaned === '')
        return null;
    try {
        const parsed = new D(cleaned);
        if (!parsed.isFinite())
            return null;
        return parsed.toFixed(decimals);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=money.js.map